<template>
  <div class="requisite-form">
    <div class="form-row">
      <div class="form-group form-group--wide">
        <base-label :required="true">Название</base-label>
        <base-input
          v-model="form.name"
          placeholder="Название пользовательского поля"
          :invalid="errors.name"
          @input="clearError('name')"
        />
        <span v-if="errors.name" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="form-group form-group--wide">
        <base-label>Описание</base-label>
        <base-input
          v-model="form.description"
          placeholder="Краткое описание"
        />
      </div>

      <div class="form-group form-group--wide">
        <base-label>Доступные модели</base-label>
        <div class="models-selector">
          <base-checkbox
            v-for="model in availableModels"
            :key="model.value"
            :model-value="form.allowed_models.includes(model.value)"
            @update:modelValue="toggleModel(model.value, $event)"
          >
            {{ model.label }}
          </base-checkbox>
        </div>
      </div>

      <div class="form-group form-group--wide form-group--attachments">
        <base-label>Прикрепления</base-label>
        <base-checkbox v-model="form.allow_attachments">
          Разрешить прикрепление файлов
        </base-checkbox>
      </div>
    </div>

    <!-- Поля схемы -->
    <div class="schema-section">
      <h4 class="schema-title">Поля</h4>

      <div
        v-for="(field, index) in form.schema.fields"
        :key="index"
        class="schema-field"
      >
        <div class="schema-field-inner">
          <div class="schema-field-selects">
            <base-select
              v-model="field.type"
              :options="fieldTypeOptions"
              :clearable="false"
              placeholder="Тип поля"
            />
            <base-input
              v-model="field.label"
              placeholder="Название поля"
              :invalid="!!(errors.fields && errors.fields[index])"
              @input="clearFieldError(index)"
            />
            <span
              v-if="errors.fields && errors.fields[index]"
              class="error-message"
            >
              Укажите название поля
            </span>
          </div>
          <base-button
            type="button"
            @click="confirmRemoveField(index)"
            class="remove-btn"
            aria-label="Удалить вариант"
          />
        </div>

        <!-- Опции для select-поля -->
        <div v-if="field.type === 'select'" class="options-editor">
          <div
            v-for="(option, idx) in field.options || []"
            :key="idx"
            class="option-row"
          >
            <div class="form-group option-group">
              <base-label class="schema-field-label">
                Вариант
              </base-label>
              <base-input
                v-model="option.label"
                placeholder="Название варианта"
              />
            </div>
            <button
              type="button"
              class="btn-remove-option"
              @click="removeOption(field, idx)"
              aria-label="Удалить вариант"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <base-button
            type="button"
            @click="addOption(field)"
            class="btn-add-option"
          >
            Добавить вариант
          </base-button>
        </div>
      </div>

      <base-button
        type="button"
        @click="addField"
        class="btn-add-field"
      >
        Добавить поле
      </base-button>
    </div>

    <div class="form-actions">
      <base-primary-button
        @click="save"
        :loading="isLoading"
        class="btn-sm"
      >
        {{ submitLabel }}
      </base-primary-button>
      <base-button @click="$emit('cancel')" class="btn-sm">
        Отмена
      </base-button>
    </div>

    <!-- Диалог подтверждения удаления поля -->
    <base-dialog
      :visible="deleteDialog.visible"
      title="Удаление поля"
      :message="
        deleteDialog.fieldLabel
          ? `Вы уверены, что хотите удалить поле «${deleteDialog.fieldLabel}»?`
          : 'Вы уверены, что хотите удалить это поле?'
      "
      :actions="[
        { key: 'confirm', label: 'Удалить', type: 'danger' },
        { key: 'close', label: 'Отмена', type: 'secondary' },
      ]"
      @action="handleDeleteDialogAction"
    />
  </div>
</template>

<script>
import { mapState } from "pinia";
import { useRequisiteStore } from "@/stores/requisiteStore";
import { useNotificationStore } from "@/stores/notificationStore";

export default {
  name: "RequisiteForm",

  props: {
    initialData: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    submitLabel: { type: String, default: "Сохранить" },
  },

  emits: ["save", "cancel"],

  data() {
    return {
      form: {
        name: "",
        description: "",
        allow_attachments: false,
        allowed_models: [],
        schema: { fields: [] },
      },
      errors: {
        name: false,
        fields: {},
      },
      deleteDialog: {
        visible: false,
        fieldIndex: null,
        fieldLabel: "",
      },
    };
  },

  computed: {
    ...mapState(useRequisiteStore, [
      "availableModels",
      "fieldTypeOptions",
    ]),

    notificationStore() {
      return useNotificationStore();
    },
  },

  methods: {
    initForm() {
      if (!this.initialData) return;
      this.form = {
        name: this.initialData.name || "",
        description: this.initialData.description || "",
        allow_attachments:
          this.initialData.allow_attachments || false,
        allowed_models: [...(this.initialData.allowed_models || [])],
        schema: {
          fields: JSON.parse(
            JSON.stringify(this.initialData.schema?.fields || [])
          ),
        },
      };
    },

    toggleModel(value, checked) {
      if (checked) {
        if (!this.form.allowed_models.includes(value)) {
          this.form.allowed_models.push(value);
        }
      } else {
        this.form.allowed_models = this.form.allowed_models.filter(
          (m) => m !== value
        );
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = false;
      }
    },

    clearFieldError(index) {
      if (this.errors.fields) {
        this.errors.fields[index] = false;
      }
    },

    validate() {
      let isValid = true;
      this.errors = { name: false, fields: {} };

      if (!String(this.form.name).trim()) {
        this.errors.name = true;
        isValid = false;
      }

      this.form.schema.fields.forEach((field, index) => {
        if (!String(field.label).trim()) {
          this.errors.fields[index] = true;
          isValid = false;
        }
      });

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
      }

      return isValid;
    },

    addField() {
      this.form.schema.fields.push({
        code: "",
        type: "string",
        label: "",
      });
    },

    confirmRemoveField(index) {
      const field = this.form.schema.fields[index];
      this.deleteDialog = {
        visible: true,
        fieldIndex: index,
        fieldLabel: field.label || "",
      };
    },

    handleDeleteDialogAction(key) {
      if (key === "confirm") {
        this.form.schema.fields.splice(
          this.deleteDialog.fieldIndex,
          1
        );
      }
      this.deleteDialog = {
        visible: false,
        fieldIndex: null,
        fieldLabel: "",
      };
    },

    addOption(field) {
      if (!field.options) field.options = [];
      field.options.push({ value: "", label: "" });
    },

    removeOption(field, index) {
      field.options.splice(index, 1);
    },

    save() {
      if (!this.validate()) {
        this.$nextTick(() => {
          const firstError = this.$el.querySelector(
            ".invalid, .error-message"
          );
          if (firstError) {
            const group = firstError.closest(".form-group");
            (group || firstError).scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
        return;
      }

      this.$emit("save", {
        name: this.form.name,
        description: this.form.description,
        allow_attachments: this.form.allow_attachments,
        allowed_models: this.form.allowed_models,
        schema: { fields: this.form.schema.fields },
      });
    },
  },

  created() {
    this.initForm();
  },
};
</script>

<style scoped>
.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 20px;
  align-items: start;
  margin-bottom: var(--base-in-indent);
}

.form-group {
  margin-bottom: 20px;
}

.form-group--wide {
  grid-column: 1 / -1;
}

.form-group--attachments {
  margin-bottom: 0;
}

.error-message {
  display: block;
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 12px;
}

.models-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
}

.schema-section {
  margin-bottom: var(--base-in-indent);
}

.schema-title {
  margin-bottom: var(--base-in-indent);
}

.schema-field {
  margin-bottom: 10px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid var(--color-grey);
}

.schema-field-inner {
  display: flex;
  align-items: flex-start;
  gap: var(--base-in-indent);
}

.schema-field-selects {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.schema-field-label {
  margin-bottom: 5px !important;
}

.remove-btn {
  margin-top: 4px;
  background-color: var(--color-danger) !important;
  color: #fff !important;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 0;
}

.remove-btn::after {
  content: "✕";
  font-size: 14px;
  line-height: 1;
}

.remove-btn:hover {
  background-color: var(--color-danger-hover) !important;
}

.options-editor {
  margin-top: 12px;
  padding-left: 16px;
  border-left: 2px solid var(--color-grey);
}

.option-row {
  display: grid;
  grid-template-columns: 1fr 34px;
  gap: 10px;
  align-items: start;
  margin-bottom: 8px;
}

.option-group {
  margin-bottom: 0;
}

.btn-remove-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: none;
  border-radius: 6px;
  background-color: #fee2e2;
  color: var(--color-danger);
  cursor: pointer;
  margin-top: 23px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.btn-remove-option:hover {
  background-color: var(--color-danger);
  color: #fff;
}

.btn-add-option {
  margin-top: 8px;
  font-size: 13px;
}

.btn-add-field {
  margin-top: 10px;
  font-size: 13px;
  background-color: var(--color-blue) !important;
  color: #fff !important;
}

.btn-add-field:hover {
  background-color: var(--color-blue-hover) !important;
}

.form-actions {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 14px;
  font-size: 13px;
}

@media screen and (max-width: 1023px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .form-group--wide {
    grid-column: span 1;
  }
}

@media screen and (max-width: 767px) {
  .schema-field-inner {
    flex-direction: row;
    align-items: flex-start;
  }
}
</style>
